import type { QueryParamsList, QueryParamsRetrieve } from '../query'
import type { ListResponse, Resource, ResourceCreate, ResourceId, ResourceRel, ResourceSort, /* ResourceFilter */ ResourcesConfig, ResourceUpdate, } from '../resource'
import { ApiResource } from '../resource'
import type { AdyenPayment, AdyenPaymentType } from './adyen_payments'
import type { EventStore } from './event_stores'
import type { PaymentMethod } from './payment_methods'
import type { Version } from './versions'


type AdyenGatewayType = 'adyen_gateways'
type AdyenGatewayRel = ResourceRel & { type: AdyenGatewayType }
type AdyenPaymentRel = ResourceRel & { type: AdyenPaymentType }


export type AdyenGatewaySort = Pick<AdyenGateway, 'id' | 'name' | 'disabled_at'> & ResourceSort
// export type AdyenGatewayFilter = Pick<AdyenGateway, 'id' | 'name' | 'disabled_at'> & ResourceFilter


interface AdyenGateway extends Resource {
	
	readonly type: AdyenGatewayType

	/** 
	 * The payment gateway's internal name.
	 * @example ```"US payment gateway"```
	 */
	name: string
	/** 
	 * Indicates if the payment source is forced on the editable order upon receiving a successful event from the gateway.
	 * @example ```true```
	 */
	force_payments?: boolean | null
	/** 
	 * Time at which this resource was disabled.
	 * @example ```"2018-01-01T12:00:00.000Z"```
	 */
	disabled_at?: string | null
	/** 
	 * The prefix of the endpoint used for live transactions.
	 * @example ```"1797a841fbb37ca7-AdyenDemo"```
	 */
	live_url_prefix: string
	/** 
	 * The checkout API version, supported range is from 66 to 71, default is 71.
	 * @example ```71```
	 */
	api_version?: number | null
	/** 
	 * Indicates if the gateway will leverage on the Adyen notification webhooks, using latest API version.
	 * @example ```true```
	 */
	async_api?: boolean | null
	/** 
	 * Send this attribute if you want to enable partial payments for the order.
	 */
	partial_payments?: boolean | null
	/** 
	 * The minutes after which the order created for partial payments automatically expires.
	 * @example ```30```
	 */
	order_expiration_mins?: number | null
	/** 
	 * Indicates if the gateway will use the native customer payment sources.
	 * @example ```true```
	 */
	native_customer_payment_sources?: boolean | null
	/** 
	 * The gateway webhook endpoint secret, generated by Adyen customer area.
	 * @example ```"xxxx-yyyy-zzzz"```
	 */
	webhook_endpoint_secret?: string | null
	/** 
	 * The gateway webhook URL, generated automatically.
	 * @example ```"https://core.commercelayer.co/webhook_callbacks/adyen_gateways/xxxxx"```
	 */
	webhook_endpoint_url?: string | null

	payment_methods?: PaymentMethod[] | null
	versions?: Version[] | null
	event_stores?: EventStore[] | null
	adyen_payments?: AdyenPayment[] | null

}


interface AdyenGatewayCreate extends ResourceCreate {
	
	/** 
	 * The payment gateway's internal name.
	 * @example ```"US payment gateway"```
	 */
	name: string
	/** 
	 * Indicates if the payment source is forced on the editable order upon receiving a successful event from the gateway.
	 * @example ```true```
	 */
	force_payments?: boolean | null
	/** 
	 * Send this attribute if you want to mark this resource as disabled.
	 * @example ```true```
	 */
	_disable?: boolean | null
	/** 
	 * Send this attribute if you want to mark this resource as enabled.
	 * @example ```true```
	 */
	_enable?: boolean | null
	/** 
	 * The gateway merchant account.
	 * @example ```"xxxx-yyyy-zzzz"```
	 */
	merchant_account: string
	/** 
	 * The gateway API key.
	 * @example ```"xxxx-yyyy-zzzz"```
	 */
	api_key: string
	/** 
	 * The public key linked to your API credential.
	 * @example ```"xxxx-yyyy-zzzz"```
	 */
	public_key?: string | null
	/** 
	 * The prefix of the endpoint used for live transactions.
	 * @example ```"1797a841fbb37ca7-AdyenDemo"```
	 */
	live_url_prefix: string
	/** 
	 * The checkout API version, supported range is from 66 to 71, default is 71.
	 * @example ```71```
	 */
	api_version?: number | null
	/** 
	 * Indicates if the gateway will leverage on the Adyen notification webhooks, using latest API version.
	 * @example ```true```
	 */
	async_api?: boolean | null
	/** 
	 * Send this attribute if you want to enable partial payments for the order.
	 */
	partial_payments?: boolean | null
	/** 
	 * The minutes after which the order created for partial payments automatically expires.
	 * @example ```30```
	 */
	order_expiration_mins?: number | null
	/** 
	 * Indicates if the gateway will use the native customer payment sources.
	 * @example ```true```
	 */
	native_customer_payment_sources?: boolean | null
	/** 
	 * The gateway webhook endpoint secret, generated by Adyen customer area.
	 * @example ```"xxxx-yyyy-zzzz"```
	 */
	webhook_endpoint_secret?: string | null

	adyen_payments?: AdyenPaymentRel[] | null

}


interface AdyenGatewayUpdate extends ResourceUpdate {
	
	/** 
	 * The payment gateway's internal name.
	 * @example ```"US payment gateway"```
	 */
	name?: string | null
	/** 
	 * Indicates if the payment source is forced on the editable order upon receiving a successful event from the gateway.
	 * @example ```true```
	 */
	force_payments?: boolean | null
	/** 
	 * Send this attribute if you want to mark this resource as disabled.
	 * @example ```true```
	 */
	_disable?: boolean | null
	/** 
	 * Send this attribute if you want to mark this resource as enabled.
	 * @example ```true```
	 */
	_enable?: boolean | null
	/** 
	 * The gateway merchant account.
	 * @example ```"xxxx-yyyy-zzzz"```
	 */
	merchant_account?: string | null
	/** 
	 * The gateway API key.
	 * @example ```"xxxx-yyyy-zzzz"```
	 */
	api_key?: string | null
	/** 
	 * The public key linked to your API credential.
	 * @example ```"xxxx-yyyy-zzzz"```
	 */
	public_key?: string | null
	/** 
	 * The prefix of the endpoint used for live transactions.
	 * @example ```"1797a841fbb37ca7-AdyenDemo"```
	 */
	live_url_prefix?: string | null
	/** 
	 * The checkout API version, supported range is from 66 to 71, default is 71.
	 * @example ```71```
	 */
	api_version?: number | null
	/** 
	 * Indicates if the gateway will leverage on the Adyen notification webhooks, using latest API version.
	 * @example ```true```
	 */
	async_api?: boolean | null
	/** 
	 * Send this attribute if you want to enable partial payments for the order.
	 */
	partial_payments?: boolean | null
	/** 
	 * The minutes after which the order created for partial payments automatically expires.
	 * @example ```30```
	 */
	order_expiration_mins?: number | null
	/** 
	 * Indicates if the gateway will use the native customer payment sources.
	 * @example ```true```
	 */
	native_customer_payment_sources?: boolean | null
	/** 
	 * The gateway webhook endpoint secret, generated by Adyen customer area.
	 * @example ```"xxxx-yyyy-zzzz"```
	 */
	webhook_endpoint_secret?: string | null

	adyen_payments?: AdyenPaymentRel[] | null

}


class AdyenGateways extends ApiResource<AdyenGateway> {

	static readonly TYPE: AdyenGatewayType = 'adyen_gateways' as const

	async create(resource: AdyenGatewayCreate, params?: QueryParamsRetrieve<AdyenGateway>, options?: ResourcesConfig): Promise<AdyenGateway> {
		return this.resources.create<AdyenGatewayCreate, AdyenGateway>({ ...resource, type: AdyenGateways.TYPE }, params, options)
	}

	async update(resource: AdyenGatewayUpdate, params?: QueryParamsRetrieve<AdyenGateway>, options?: ResourcesConfig): Promise<AdyenGateway> {
		return this.resources.update<AdyenGatewayUpdate, AdyenGateway>({ ...resource, type: AdyenGateways.TYPE }, params, options)
	}

	async delete(id: string | ResourceId, options?: ResourcesConfig): Promise<void> {
		await this.resources.delete((typeof id === 'string')? { id, type: AdyenGateways.TYPE } : id, options)
	}

	async payment_methods(adyenGatewayId: string | AdyenGateway, params?: QueryParamsList<PaymentMethod>, options?: ResourcesConfig): Promise<ListResponse<PaymentMethod>> {
		const _adyenGatewayId = (adyenGatewayId as AdyenGateway).id || adyenGatewayId as string
		return this.resources.fetch<PaymentMethod>({ type: 'payment_methods' }, `adyen_gateways/${_adyenGatewayId}/payment_methods`, params, options) as unknown as ListResponse<PaymentMethod>
	}

	async versions(adyenGatewayId: string | AdyenGateway, params?: QueryParamsList<Version>, options?: ResourcesConfig): Promise<ListResponse<Version>> {
		const _adyenGatewayId = (adyenGatewayId as AdyenGateway).id || adyenGatewayId as string
		return this.resources.fetch<Version>({ type: 'versions' }, `adyen_gateways/${_adyenGatewayId}/versions`, params, options) as unknown as ListResponse<Version>
	}

	async event_stores(adyenGatewayId: string | AdyenGateway, params?: QueryParamsList<EventStore>, options?: ResourcesConfig): Promise<ListResponse<EventStore>> {
		const _adyenGatewayId = (adyenGatewayId as AdyenGateway).id || adyenGatewayId as string
		return this.resources.fetch<EventStore>({ type: 'event_stores' }, `adyen_gateways/${_adyenGatewayId}/event_stores`, params, options) as unknown as ListResponse<EventStore>
	}

	async adyen_payments(adyenGatewayId: string | AdyenGateway, params?: QueryParamsList<AdyenPayment>, options?: ResourcesConfig): Promise<ListResponse<AdyenPayment>> {
		const _adyenGatewayId = (adyenGatewayId as AdyenGateway).id || adyenGatewayId as string
		return this.resources.fetch<AdyenPayment>({ type: 'adyen_payments' }, `adyen_gateways/${_adyenGatewayId}/adyen_payments`, params, options) as unknown as ListResponse<AdyenPayment>
	}

	async _disable(id: string | AdyenGateway, params?: QueryParamsRetrieve<AdyenGateway>, options?: ResourcesConfig): Promise<AdyenGateway> {
		return this.resources.update<AdyenGatewayUpdate, AdyenGateway>({ id: (typeof id === 'string')? id: id.id, type: AdyenGateways.TYPE, _disable: true }, params, options)
	}

	async _enable(id: string | AdyenGateway, params?: QueryParamsRetrieve<AdyenGateway>, options?: ResourcesConfig): Promise<AdyenGateway> {
		return this.resources.update<AdyenGatewayUpdate, AdyenGateway>({ id: (typeof id === 'string')? id: id.id, type: AdyenGateways.TYPE, _enable: true }, params, options)
	}


	isAdyenGateway(resource: any): resource is AdyenGateway {
		return resource.type && (resource.type === AdyenGateways.TYPE)
	}


	relationship(id: string | ResourceId | null): AdyenGatewayRel {
		return super.relationshipOneToOne<AdyenGatewayRel>(id)
	}

	relationshipToMany(...ids: string[]): AdyenGatewayRel[] {
		return super.relationshipOneToMany<AdyenGatewayRel>(...ids)
	}


	type(): AdyenGatewayType {
		return AdyenGateways.TYPE
	}

}


const instance = new AdyenGateways()
export default instance

export type { AdyenGateway, AdyenGatewayCreate, AdyenGateways, AdyenGatewayType, AdyenGatewayUpdate }
